<script lang="ts" module>
	interface PreloadFont {
		type: string;
		href: string;
	}
</script>

<script lang="ts">
	import defaultOgImage from "@assets/images/og-image.jpeg";
	import BreadcrumbListStructuredData from "@components/BreadcrumbListStructuredData.svelte";
	import OrganizationStructuredData from "@components/OrganizationStructuredData.svelte";
	import { createTitle } from "@utils/createTitle.js";
	import type { Snippet } from "svelte";
	import resolveConfig from "tailwindcss/resolveConfig.js";
	import DrukCondensedSuper from "@core/assets/fonts/DrukCondensed/DrukCondensed-Super.woff2";
	import MontSerratBold from "@core/assets/fonts/Montserrat/Montserrat-Bold.woff2";
	import MontSerratMedium from "@core/assets/fonts/Montserrat/Montserrat-Medium.woff2";
	import MontSerratRegular from "@core/assets/fonts/Montserrat/Montserrat-Regular.woff2";
	import { tenants } from "@core/config/tenants.js";
	import type { Media } from "@core/schema/Media.js";
	import { appName } from "@core/schema/names/appName.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import tailwindConfig from "../../../tailwind.config.js";
	import "../../assets/css/main.css";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import Breadcrumbs from "./Breadcrumbs.svelte";
	import Footer from "./Footer.svelte";
	import GoogleTagManager from "./GoogleTagManager.svelte";
	import HeadFavicons from "./HeadFavicons.svelte";
	import Header from "./Header.svelte";

	const {
		ogImage = defaultOgImage,
		children,
		stickyBottom,
	}: {
		ogImage?: string | Media;
		children: Snippet;
		stickyBottom?: Snippet;
	} = $props();

	const { ROBOTS, WEB_URL, FILE_BUCKET_URL_MAP } = getGlobalSettings();
	const { breadcrumbs, pagePath, title, description, keywords, index, tenant } = getGlobalTemplateProps();

	const pageTitle = createTitle(title);

	const fontsToPreload: PreloadFont[] = [
		{ href: DrukCondensedSuper, type: "font/woff2" },
		{ href: MontSerratBold, type: "font/woff2" },
		{ href: MontSerratMedium, type: "font/woff2" },
		{ href: MontSerratRegular, type: "font/woff2" },
	];
	const canonical = tenant.locale in pagePath ? `${WEB_URL}${pagePath[tenant.locale]}` : undefined;
	const themeColor = resolveConfig(tailwindConfig).theme.colors.white;
	const ogImagePath = $derived(
		typeof ogImage === "string" ? ogImage : thumbUploadedFile(1200, 630, ogImage, FILE_BUCKET_URL_MAP, null),
	);

	$effect(() => {
		document.documentElement.style.overflow = lockScroll.lock ? "hidden" : "auto";
	});
</script>

<svelte:head>
	<!-- Preloads of fonts used at the top of pages -->
	{#each fontsToPreload as { href, type } (href)}
		<link rel="preload" as="font" {href} {type} crossorigin="anonymous" />
	{/each}

	<!-- Theme color -->
	<meta name="msapplication-TileColor" content={themeColor} />
	<meta name="theme-color" content={themeColor} />

	<!-- Site name -->
	<meta property="og:site_name" content={appName} />
	<meta name="twitter:site" content={appName} />
	<meta name="application-name" content={appName} />
	<meta name="apple-mobile-web-app-title" content={appName} />

	<!-- Dynamic Open graph tags (Meta) -->
	{#if canonical}
		<meta property="og:url" content={canonical} />
	{/if}

	{#if ogImagePath}
		<meta property="og:image" content={ogImagePath} />
	{/if}

	<!-- Dynamic Twitter Meta tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="twitter:domain" content={WEB_URL} />
	{#if canonical}
		<meta property="twitter:url" content={canonical} />
	{/if}

	{#if ogImagePath}
		<meta property="twitter:image" content={ogImagePath} />
	{/if}

	<!-- Title, description and keywords -->
	<title>{pageTitle}</title>
	<meta property="title" content={pageTitle} />
	<meta property="og:title" content={pageTitle} />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="description" content={description} />
	<meta name="og:description" content={description} />
	<meta name="twitter:description" content={description} />
	{#if keywords}
		<meta name="keywords" content={keywords} />
	{/if}

	<!-- Robots -->
	<meta name="robots" content={ROBOTS === "index" && index ? "index, follow" : "noindex, nofollow"} />

	<!-- Canonical and hreflang -->
	{#if canonical}
		<link rel="canonical" href={canonical} />
	{/if}
	{#each Object.values(tenants) as tenant (tenant.locale)}
		{#if tenant.locale in pagePath}
			<link rel="alternate" hreflang={tenant.locale} href="{WEB_URL}{pagePath[tenant.locale]}" />
		{/if}
	{/each}
</svelte:head>

<HeadFavicons />
<GoogleTagManager />
<OrganizationStructuredData />
<BreadcrumbListStructuredData {breadcrumbs} />

<div class="contents">
	<Header />

	<main class="grid-container grow relative">
		<Breadcrumbs {breadcrumbs} />

		{@render children()}
	</main>

	<Footer {stickyBottom} />
</div>
